import { useState, useEffect } from "react";
import api, { formatApiError } from "@/lib/api";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { UserPlus } from "@phosphor-icons/react";
import { SearchableSelect } from "@/components/SearchableSelect";
import { toast } from "sonner";

const FIELDS = [
  ["staff_id", "Staff ID", "text", true],
  ["ssnit_number", "SSNIT Number", "text", true],
  ["first_name", "First name", "text", true],
  ["middle_name", "Middle name", "text", false],
  ["last_name", "Last name", "text", true],
  ["date_of_birth", "Date of birth", "date", true],
  ["contact_number", "Contact number", "text", true],
  ["email", "Email address", "email", true],
];

const empty = FIELDS.reduce((a, [k]) => ({ ...a, [k]: "" }), { sex: "", facility_id: "" });

export function AddMemberDialog({ user, facilities, onAdded }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [busy, setBusy] = useState(false);
  const [facs, setFacs] = useState([]);
  const isFacilityMgr = user.role === "facility_manager";

  useEffect(() => {
    if (open && !isFacilityMgr) {
      api.get("/members/addable-facilities").then((r) => setFacs(r.data.facilities || [])).catch(() => setFacs([]));
    }
  }, [open, isFacilityMgr]);

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const submit = async () => {
    setBusy(true);
    try {
      const payload = { ...form };
      if (isFacilityMgr) payload.facility_id = user.facility_id;
      await api.post("/members", payload);
      toast.success("Member added — they must complete their profile before it goes for approval");
      setForm(empty);
      setOpen(false);
      onAdded?.();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) setForm(empty); }}>
      <DialogTrigger asChild>
        <Button className="gap-2" data-testid="add-member-button">
          <UserPlus size={18} weight="bold" /> Add Member
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto form-contrast" data-testid="add-member-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-xl">Add a new member</DialogTitle>
          <DialogDescription>
            The member signs in with their SSNIT number as the initial password, sets a new password, then
            completes their profile (including a profile photo). Only after that does it go for approval.
          </DialogDescription>
        </DialogHeader>
        <div className="grid sm:grid-cols-2 gap-4 mt-2">
          {!isFacilityMgr && (
            <div className="space-y-1.5 sm:col-span-2">
              <Label>Facility <span className="text-destructive">*</span></Label>
              <SearchableSelect
                value={form.facility_id}
                onChange={(v) => set("facility_id", v)}
                options={facs.map((f) => f.name)}
                showAll={false}
                placeholder={facs.length ? "Search and select facility" : "No facilities in your scope"}
                testId="add-member-facility"
                className="w-full"
              />
            </div>
          )}
          {FIELDS.map(([key, label, type, req]) => (
            <div key={key} className="space-y-1.5">
              <Label>{label}{req && <span className="text-destructive"> *</span>}</Label>
              <Input data-testid={`add-member-${key}`} type={type} value={form[key]}
                onChange={(e) => set(key, e.target.value)} />
            </div>
          ))}
          <div className="space-y-1.5">
            <Label>Sex <span className="text-destructive">*</span></Label>
            <Select value={form.sex} onValueChange={(v) => set("sex", v)}>
              <SelectTrigger data-testid="add-member-sex"><SelectValue placeholder="Select" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="Male">Male</SelectItem>
                <SelectItem value="Female">Female</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter className="mt-3">
          <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
          <Button onClick={submit} disabled={busy} data-testid="add-member-submit">
            {busy ? "Adding…" : "Add member"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
